<template>
  <hb-basic-page
    :action-buttons="$vuetify.breakpoint.smAndDown ? {} : {}"
    :back-button="{ show: true, fallback: 'tenant_reports' }"
    :extension-height="$vuetify.breakpoint.xsOnly ? 112 : 120"
    :fill-height="surveys.length === 0"
    :loading="loading"
    :subtle-loading="subtleLoading"
    :title="$t(`reports.${category}`)"
    name="report-category-view"
  >
    <template v-if="showSurveySelectionsInAppBar" v-slot:app-bar-extension>
      <v-row no-gutters>
        <v-col cols="12" lg="10" md="10" offset-lg="1" offset-md="1" offset-xl="2" xl="8">
          <surveys-selector :app-bar="true" :management-surveys="managementSurveys"
                            :personnel-surveys="personnelSurveys"/>
        </v-col>
      </v-row>
    </template>

    <v-col
      v-if="!loading && surveys.length > 0"
      v-scroll="handleScroll"
      cols="12"
      lg="10"
      md="10"
      offset-lg="1"
      offset-md="1"
      offset-xl="2"
      xl="8"
    >
      <v-row no-gutters>
        <v-col cols="12">
          <v-card color="white" light>
            <v-card-title>
              {{ $t('reports.surveys_used_for_reports') }}
            </v-card-title>
            <v-card-text>
              <div class="pb-5 text-body-2 text-sm-body-1">
                {{ $t('reports.you_can_choose_which_analysis_are_used') }}
              </div>
              <div ref="surveySelectionEndMarker"></div>
              <v-container class="px-0" fluid>
                <surveys-selector :management-surveys="managementSurveys" :personnel-surveys="personnelSurveys"/>

                <v-alert v-if="!selectedManagementSurvey && !selectedPersonnelSurvey" class="mt-8 mb-0" prominent text
                         type="info">
                  {{ $t('reports.you_must_choose_atleast_one_analysis') }}
                </v-alert>
              </v-container>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <template
        v-if="(selectedManagementSurvey && ($isPaidTenant || selectedManagementSurveyShares.length > 0)) || (selectedPersonnelSurvey && ($isPaidTenant || selectedPersonnelSurveyShares.length > 0))">
        <v-row v-if="category === 'capability' || category === 'efficiency'">
          <v-col class="font-italic text-subtitle-1 text-md-h6 font-weight-bold pt-3 pb-6 pt-md-8 pb-md-12" cols="12">
            {{ $t('reports.category_abstract.' + category) }}
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-row>
              <v-col v-if="category === 'culture'" cols="12">
                <v-card>
                  <v-card-title>
                    <section-header>
                      {{ $t('dashboard.company_culture') }}
                    </section-header>
                  </v-card-title>
                  <v-card-text class="text-body-2 text-sm-body-1 pb-6">
                    <v-row>
                      <v-col cols="12" order="0">
                        <p v-html="$t('reports.culture_instructions.2')"></p>
                      </v-col>
                      <v-col cols="12" order="2" order-sm="1" sm="7">
                        <p v-html="$t('reports.culture_instructions.fragmented')"></p>
                        <p v-html="$t('reports.culture_instructions.authoritative')"></p>
                        <p v-html="$t('reports.culture_instructions.individual')"></p>
                        <p v-html="$t('reports.culture_instructions.productive')"></p>
                      </v-col>
                      <v-col v-if="personnelSurvey && personnelSurvey.calculation" cols="12" order="1" sm="5">
                        <company-culture-chart :calculation="personnelSurvey.calculation"/>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-card>
                  <v-card-text>
                    <report-personnel-vs-management-chart legend/>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-row dense>
                  <template v-for="subcategory in categories.children">
                    <report-sub-category-badge
                      :key="subcategory.id"
                      :category="category"
                      :loading="subtleLoading || loading"
                      :name="subcategory.engineName"
                      :scale="scale"
                      :subcategory="subcategory"
                      :surveys="reports"
                      @on-click="scrollToSubCategory"
                    />
                  </template>
                </v-row>
              </v-col>

              <template v-for="subcategory in categories.children">
                <report-subcategory-card
                  :key="subcategory.id"
                  :ref="subcategory.engineName"
                  :category="category"
                  :loading="subtleLoading || loading"
                  :scale="scale"
                  :subcategory="subcategory"
                  :surveys="reports"
                />
              </template>
            </v-row>
          </v-col>
        </v-row>
      </template>
    </v-col>
  </hb-basic-page>
</template>

<script>
import surveyApi from '@/api/survey';
import ReportPersonnelVsManagementChart from '@/components/Tenant/ReportCategory/ReportPersonnelVsManagementChart';
import ReportSubcategoryCard from '@/components/Tenant/ReportCategory/ReportSubcategoryCard';
import categoryService from '@/service/category.service';
import ReportSubCategoryBadge from '@/components/Tenant/ReportCategory/ReportSubCategoryBadge';
import CompanyCultureChart from '@/components/Tenant/Dashboard/CompanyCultureChart';
import SectionHeader from '@/components/SectionHeader';
import dayjs from 'dayjs';
import SurveysSelector from '@/components/Tenant/Reports/SurveysSelector';
import isEqual from "lodash/isEqual";

const statusColors = {
  0: 'red',
  100: 'blue',
  200: 'success',
};

export default {
  name: 'ReportCategoryView',
  components: {
    SurveysSelector,
    SectionHeader,
    CompanyCultureChart,
    ReportSubCategoryBadge,
    ReportSubcategoryCard,
    ReportPersonnelVsManagementChart,
  },
  data() {
    return {
      loading: true,
      subtleLoading: false,
      searchPhrase: '',
      tab: 'report',
      surveys: [],
      reports: [],
      showSurveySelectionsInAppBar: false,
    };
  },
  computed: {
    personnelSurvey() {
      return this.reports?.find(s => s.type === 'PERSONNEL');
    },
    managementSurvey() {
      return this.reports?.find(s => s.type === 'MANAGEMENT');
    },
    personnelSurveys() {
      const today = dayjs();
      const personnelSurveysWithAnswers = this.surveys.filter(s => s.surveyTemplate.type === 'PERSONNEL' && s.latestAnswerAt);
      if (personnelSurveysWithAnswers.length > 0) {
        const endedPersonnelSurveys = personnelSurveysWithAnswers.filter(s => dayjs(s.end).isBefore(today));
        if (endedPersonnelSurveys.length > 0) return endedPersonnelSurveys;
        return [personnelSurveysWithAnswers[0]];
      }
      return [];
    },
    managementSurveys() {
      const today = dayjs();
      const managementSurveysWithAnswers = this.surveys.filter(s => s.surveyTemplate.type === 'MANAGEMENT' && s.latestAnswerAt);
      if (managementSurveysWithAnswers.length > 0) {
        const endedManagementSurveys = managementSurveysWithAnswers.filter(s => dayjs(s.end).isBefore(today));
        if (endedManagementSurveys.length > 0) return endedManagementSurveys;
        return [managementSurveysWithAnswers[0]];
      }
      return [];
    },
    category() {
      return this.$route.params.category;
    },
    subcategory() {
      return this.$route.params.subcategory;
    },
    categories() {
      const tree = {...categoryService.tree[this.category]};
      if (this.category === 'culture') {
        const children = {...tree.children};

        ['individual', 'productive', 'fragmented', 'authoritative'].forEach(engineName => {
          delete children[engineName];
        });
        tree.children = children;
      }
      return tree;
    },
    scale() {
      if (this.category === 'culture' || this.category === 'economy_customers') {
        return [22.5, 72.5];
      }
      return [40, 60];
    },
    selectedPersonnelSurvey: {
      get() {
        return this.$store.state.selectedPersonnelSurvey;
      },
      set(value) {
        this.$store.commit('setSelectedPersonnelSurvey', value);
      },
    },
    selectedManagementSurvey: {
      get() {
        return this.$store.state.selectedManagementSurvey;
      },
      set(value) {
        this.$store.commit('setSelectedManagementSurvey', value);
      },
    },
    selectedManagementSurveyShares() {
      return [...this.$store.state.reportFilters.selectedManagementSurveyShares]; // Vaatii kopion jotta muutokset triggeröivät watcherin oikein
    },
    selectedPersonnelSurveyShares() {
      return [...this.$store.state.reportFilters.selectedPersonnelSurveyShares]; // Vaatii kopion jotta muutokset triggeröivät watcherin oikein
    },
    personnelSurveyShares() {
      return [...this.$store.state.reportFilters.personnelSurveyShares];
    },
    managementSurveyShares() {
      return [...this.$store.state.reportFilters.managementSurveyShares];
    },
  },
  watch: {
    selectedPersonnelSurvey(to, from) {
      if (isEqual(to, from)) return;
      if (!this.loading) this.reloadReports();
    },
    selectedManagementSurvey(to, from) {
      if (isEqual(to, from)) return;
      if (!this.loading) this.reloadReports();
    },
    selectedManagementSurveyShares(to, from) {
      if (isEqual(to, from)) return;
      if (!this.loading) this.reloadReports();
    },
    selectedPersonnelSurveyShares(to, from) {
      if (isEqual(to, from)) return;
      if (!this.loading) this.reloadReports();
    },
  },
  methods: {
    handleScroll() {
      if (this.$refs.surveySelectionEndMarker && this.$vuetify.breakpoint.smAndUp) {
        this.showSurveySelectionsInAppBar = this.$refs.surveySelectionEndMarker.getBoundingClientRect().y < 0;
      } else {
        this.showSurveySelectionsInAppBar = false;
      }
    },
    scrollToSubCategory(name) {
      if (this.$refs[name]) {
        this.$vuetify.goTo(this.$refs[name][0].$el, {duration: 500, offset: 0, easing: 'easeInOutCubic'});
      }
    },
    getStatusTextColor(status) {
      return statusColors[status] + '--text';
    },
    personnelAvg(name) {
      if (!this.personnelSurvey) return -1;
      const value = this.personnelSurvey?.calculation?.engineResponse?.lta?.[this.category]?.avg?.find(avg => avg.name === name)?.value;
      return parseFloat(value).toFixed(0);
    },
    managementAvg(name) {
      if (!this.managementSurvey) return -1;
      const value = this.managementSurvey?.calculation?.engineResponse?.oja?.[this.category]?.avg?.find(avg => avg.name === name)?.value;
      return parseFloat(value).toFixed(0);
    },

    async reload() {
      if (this.surveys.length > 0) {
        this.subtleLoading = true;
        this.loading = false;
      } else {
        this.loading = true;
        this.subtleLoading = false;
      }
      try {
        let surveys = await surveyApi.getSurveys();

        this.surveys = surveys.sort((a, b) => {
          return new Date(b.end) - new Date(a.end);
        });

        if (!this.selectedPersonnelSurvey || !this.selectedManagementSurvey) {
          await this.setSelectedSurveys();
        } else {
          await this.reloadReports();
        }
      } catch (e) {
        this.$handleApiError(e, this.$t('errors.error_loading_surveys'));
      }
      this.loading = false;
      this.subtleLoading = false;
    },
    async reloadReports() {
      if (!this.loading) {
        this.subtleLoading = true;
      }
      try {
        if (this.$isPaidTenant && this.selectedManagementSurveyShares.length === 0 && this.selectedPersonnelSurveyShares.length === 0) {
          const surveys = [this.selectedPersonnelSurvey || false, this.selectedManagementSurvey || false].filter(Boolean);
          this.reports = await surveyApi.getSurveysReports(surveys);
        } else {
          let surveyShareKeys = []
          if (this.$isPaidTenant && (this.selectedManagementSurveyShares.length === 0 || this.selectedPersonnelSurveyShares.length === 0)) { // Either one of survey shares are empty, get every surveyshare (only if paid customer)
            surveyShareKeys = [
              ...(this.selectedManagementSurveyShares.length === 0 ? this.managementSurveyShares.map(s => s.key) : this.selectedManagementSurveyShares),
              ...(this.selectedPersonnelSurveyShares.length === 0 ? this.personnelSurveyShares.map(s => s.key) : this.selectedPersonnelSurveyShares)
            ].filter(Boolean);
          } else {
            surveyShareKeys = [...this.selectedManagementSurveyShares, ...this.selectedPersonnelSurveyShares].filter(Boolean);
          }

          this.reports = await surveyApi.getSurveysReportsBySurveyShareKeys(surveyShareKeys);
        }


      } catch (e) {
        this.$handleApiError(e, this.$t('errors.error_loading_reports'));
      }
      this.subtleLoading = false;
    },
    async setSelectedSurveys() {
      if (!this.selectedPersonnelSurvey) {
        const personnelSurvey = this.personnelSurveys?.[0];
        if (personnelSurvey) {
          this.$store.commit('setSelectedPersonnelSurvey', personnelSurvey.key);
        }
      }

      if (!this.selectedManagementSurvey) {
        const managementSurvey = this.managementSurveys?.[0];
        if (managementSurvey) {
          this.$store.commit('setSelectedManagementSurvey', managementSurvey.key);
        }
      }
      await this.reloadReports();
    },
  },
  async mounted() {
    this.loading = true;
    await this.reload();
    if (this.subcategory) {
      setTimeout(() => {
        this.scrollToSubCategory(this.subcategory);
      }, 500);
    }
  },
};
</script>
